import React, { useState, useEffect, useRef } from 'react';
import logoWe2 from './Logo-We2.png';
import logoWe3 from './Logo-We3.png';

const translations = {
  en: {
    unitLabel: "Unit",
    perMonth: "/mo",
    available: "Available",
    now: "NOW",
    viewPlan: "View Unit Details",
    visitUnit: "Request a Visit",
    building: "Buildings",
    bedrooms: "Bedrooms",
    bathroomsFilter: "Bathrooms",
    searchLabel: "Search",
    searchPlaceholder: "Unit #...",
    moveIn: "Move-in",
    sortBy: "Sort",
    reset: "Reset",
    showing: "Showing",
    units: "available units",
    none: "No units match your search.",
    asap: "ASAP",
    nextMonth: "Next Month",
    range23: "2-3 Months",
    studio: "Studio",
    bedLabel: "Bed",
    bedsLabel: "Beds",
    bathLabel: "Bath",
    bathsLabel: "Baths",
    default: "Default",
    priceLow: "↑ Price",
    priceHigh: "↓ Price",
    dateSoon: "Soonest",
    dateLate: "Latest",
    any: "Any",
    emailList: "Email My List",
    submit: "Send Request",
    cancel: "Cancel",
    namePlace: "Full Name",
    phonePlace: "Phone Number",
    emailPlace: "Email Address",
    validationError: "Please provide your name and either a phone number or an email.",
    successMsg: "Request sent! I will contact you shortly.",
    emailSubject: "My Favorite Apartments - WE2/WE3",
    rentLabel: "Rent",
    specsLabel: "Specs",
    availLabel: "Availability",
    linkLabel: "Link",
    contactMsg: "For any questions, or to schedule a visit contact:",
    introMsg: "Here are my favorite units from WE2/WE3:"
  },
  fr: {
    unitLabel: "Unité",
    perMonth: "/mois",
    available: "Disponible",
    now: "MAINTENANT",
    viewPlan: "Détails de l'unité",
    visitUnit: "Demander une visite",
    building: "Bâtiments",
    bedrooms: "Chambres",
    bathroomsFilter: "Salles de bain",
    searchLabel: "Recherche",
    searchPlaceholder: "Unité #...",
    moveIn: "Emménagement",
    sortBy: "Trier",
    reset: "Réinitialiser",
    showing: "Affichage de",
    units: "unités disponibles",
    none: "Aucune unité ne correspond.",
    asap: "Immédiat",
    nextMonth: "Mois prochain",
    range23: "2-3 mois",
    studio: "Studio",
    bedLabel: "ch.",
    bedsLabel: "ch.",
    bathLabel: "s.d.b.",
    bathsLabel: "s.d.b.",
    default: "Défaut",
    priceLow: "↑ Prix",
    priceHigh: "↓ Prix",
    dateSoon: "Plus tôt",
    dateLate: "Plus tard",
    any: "Tous",
    emailList: "Envoyer ma liste",
    submit: "Envoyer",
    cancel: "Annuler",
    namePlace: "Nom complet",
    phonePlace: "Téléphone",
    emailPlace: "Courriel",
    validationError: "Veuillez fournir votre nom et soit un numéro de téléphone ou un courriel.",
    successMsg: "Demande envoyée! Je vous contacterai sous peu.",
    emailSubject: "Mes appartements préférés we2 we3",
    rentLabel: "Loyer",
    specsLabel: "Spécification",
    availLabel: "Disponible",
    linkLabel: "Lien",
    contactMsg: "Pour toute question ou pour planifier une visite, contactez :",
    introMsg: "Voici mes unités favorites de WE2/WE3 :"
  }
};

const getDynamicMonths = (lang) => {
  const months = [];
  const today = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const value = d.toISOString().substring(0, 7);
    const label = d.toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-CA', { month: 'long', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
};

const formatBedrooms = (beds, lang) => {
  if (beds === undefined || beds === null) return "N/A";
  const b = beds.toString().toLowerCase();
  const t = translations[lang];
  if (b === '1.1') return `1 + Den`;
  if (b === '2.1') return `2 + Den`;
  if (b === '0' || b === 'studio') return t.studio;
  const label = b === '1' ? t.bedLabel : t.bedsLabel;
  return `${beds} ${label}`;
};

const formatFriendlyDate = (isoString, lang) => {
  if (!isoString || isoString === "N/A") return "N/A";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  const today = new Date(); today.setHours(0, 0, 0, 0); 
  const availDate = new Date(isoString); availDate.setHours(0, 0, 0, 0);
  if (availDate <= today) return <span style={{ color: '#27ae60', fontWeight: 'bold' }}>{translations[lang].now}</span>;
  return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-CA', { month: 'long', day: 'numeric', timeZone: 'UTC' });
};

export default function App() {
  const [allUnits, setAllUnits] = useState([]);
  const [displayedUnits, setDisplayedUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lang, setLang] = useState('en');
  const [favorites, setFavorites] = useState([]);
  
  // FILTERS STATE
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBuildings, setSelectedBuildings] = useState(['WE2', 'WE3']);
  const [bedFilters, setBedFilters] = useState([]);
  const [bathFilters, setBathFilters] = useState([]); 
  const [monthFilters, setMonthFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState('date-asc');
  
  // DROPDOWN TOGGLES
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isBedOpen, setIsBedOpen] = useState(false);
  const [isBathOpen, setIsBathOpen] = useState(false); 
  
  // MODAL STATE
  const [showModal, setShowModal] = useState(false);
  const [targetUnit, setTargetUnit] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const monthRef = useRef(null);
  const bedRef = useRef(null);
  const bathRef = useRef(null); 
  
  const t = translations[lang];
  const dynamicMonths = getDynamicMonths(lang);
  const GOOGLE_API_URL = 'https://script.google.com/macros/s/AKfycbwZbcR619ZiAussBxFEoOB1EGXihhypyz5BWu34JHeF0mdW8cv6gfEX5NFNTm_a2kOG/exec';

  const fetchUnits = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(GOOGLE_API_URL);
      const data = await response.json();
      setAllUnits(data);
      setDisplayedUnits(data);
      setIsLoading(false);
    } catch (err) { setIsLoading(false); }
  };

  const handleEmailFavorites = () => {
    const selectedData = allUnits.filter(u => favorites.includes(u.UnitNumber));
    
    let body = `${t.introMsg}\n\n`;
    
    selectedData.forEach(u => {
      const emailDate = formatFriendlyDate(u.AvailabilityDate, lang);
      const cleanDate = typeof emailDate === 'string' ? emailDate : emailDate.props.children;

      body += `---------------------------\n`;
      body += `${t.unitLabel}: ${u.UnitNumber} (${u.Building.includes('3') ? 'WE3' : 'WE2'})\n`;
      body += `${t.rentLabel}: $${u.Rent}${t.perMonth}\n`;
      body += `${t.specsLabel}: ${formatBedrooms(u.Bedrooms, lang)}, ${u.Bathrooms} ${u.Bathrooms.toString() === '1' ? t.bathLabel : t.bathsLabel}, ${u.SquareFootage} ${lang === 'en' ? 'sqft' : 'pi²'}\n`;
      body += `${t.availLabel}: ${cleanDate}\n`;
      body += `${t.linkLabel}: ${u.UnitURL}\n\n`;
    });

    body += `\n${t.contactMsg}\n\n`;
    body += `MATHIEU CLARK\n`;
    body += `Agent de location / Leasing Agent\n`;
    body += `M : 819-744-7006\n`;
    body += `Le Groupe Heafey | The Heafey Group\n`;
    body += `45 rue Eddy\n`;
    body += `Gatineau, QC J8X 2V8\n`;
    body += `mclark@groupeheafey.com`;

    const subject = encodeURIComponent(t.emailSubject);
    const bccEmail = "mclark@groupeheafey.com";
    const mailtoLink = `mailto:?subject=${subject}&bcc=${bccEmail}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const toggleFavorite = (unitNum) => setFavorites(prev => prev.includes(unitNum) ? prev.filter(id => id !== unitNum) : [...prev, unitNum]);
  const toggleBuilding = (name) => setSelectedBuildings(prev => prev.includes(name) ? prev.filter(b => b !== name) : [...prev, name]);
  const toggleBed = (e, val) => { e.stopPropagation(); setBedFilters(prev => prev.includes(val) ? prev.filter(b => b !== val) : [...prev, val]); };
  const toggleBath = (e, val) => { e.stopPropagation(); setBathFilters(prev => prev.includes(val) ? prev.filter(b => b !== val) : [...prev, val]); };
  const toggleMonth = (e, val) => { e.stopPropagation(); setMonthFilters(prev => prev.includes(val) ? prev.filter(m => m !== val) : [...prev, val]); };

  const handleVisitRequest = async (e) => {
    e.preventDefault();
    if (!formData.name || (!formData.phone && !formData.email)) {
      alert(t.validationError);
      return;
    }
    setIsSubmitting(true);
    try {
      await fetch(GOOGLE_API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, unit: targetUnit.UnitNumber, building: targetUnit.Building })
      });
      alert(t.successMsg);
      setShowModal(false);
      setFormData({ name: '', phone: '', email: '' });
    } catch (err) { alert("Error."); }
    setIsSubmitting(false);
  };

  useEffect(() => {
    fetchUnits();
    const handleClickOutside = (e) => {
      if (monthRef.current && !monthRef.current.contains(e.target)) setIsMonthOpen(false);
      if (bedRef.current && !bedRef.current.contains(e.target)) setIsBedOpen(false);
      if (bathRef.current && !bathRef.current.contains(e.target)) setIsBathOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let filtered = [...allUnits];
    
    // 0. Search Filter
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(u => 
        u.UnitNumber && u.UnitNumber.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // 1. Building Filter
    if (selectedBuildings.length > 0) {
      filtered = filtered.filter(u => {
        if (!u.Building) return false;
        const bName = u.Building.toString().toUpperCase();
        return selectedBuildings.some(selected => bName.includes(selected.toUpperCase()));
      });
    }

    // 2. Bedrooms Filter
    if (bedFilters.length > 0) {
      filtered = filtered.filter(u => u.Bedrooms !== undefined && u.Bedrooms !== null && bedFilters.includes(u.Bedrooms.toString().toLowerCase()));
    }

    // 3. Bathrooms Filter
    if (bathFilters.length > 0) {
      filtered = filtered.filter(u => u.Bathrooms !== undefined && u.Bathrooms !== null && bathFilters.includes(u.Bathrooms.toString()));
    }

    // 4. Move-In Filter
    if (monthFilters.length > 0) {
      filtered = filtered.filter(u => {
        if (!u.AvailabilityDate || u.AvailabilityDate === "N/A") return false;
        const uMonth = u.AvailabilityDate.substring(0, 7);
        return monthFilters.some(filter => {
          if (filter === 'ASAP') return uMonth <= new Date().toISOString().substring(0, 7);
          if (filter === 'NEXT') {
            const d = new Date(); d.setMonth(d.getMonth() + 1);
            return uMonth <= d.toISOString().substring(0, 7);
          }
          if (filter === 'RANGE') {
            const d3 = new Date(); d3.setMonth(d3.getMonth() + 3);
            return uMonth <= d3.toISOString().substring(0, 7);
          }
          return uMonth <= filter;
        });
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      if (sortOrder === 'price-asc') return parseFloat(String(a.Rent).replace('$', '')) - parseFloat(String(b.Rent).replace('$', ''));
      if (sortOrder === 'price-desc') return parseFloat(String(b.Rent).replace('$', '')) - parseFloat(String(a.Rent).replace('$', ''));
      if (sortOrder === 'date-asc') return a.AvailabilityDate.localeCompare(b.AvailabilityDate);
      if (sortOrder === 'date-desc') return b.AvailabilityDate.localeCompare(a.AvailabilityDate);
      return 0;
    });
    setDisplayedUnits(filtered);
  }, [searchQuery, selectedBuildings, bedFilters, bathFilters, monthFilters, sortOrder, allUnits, lang]);

  if (isLoading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{t.showing} {displayedUnits.length} {t.units}</h1>
        <div style={{ background: '#eee', borderRadius: '20px', padding: '4px' }}>
          <button onClick={() => setLang('en')} style={{ border: 'none', background: lang === 'en' ? '#fff' : 'transparent', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold' }}>EN</button>
          <button onClick={() => setLang('fr')} style={{ border: 'none', background: lang === 'fr' ? '#fff' : 'transparent', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold' }}>FR</button>
        </div>
      </div>

      {/* FILTERS */}
      <div style={{ display: 'flex', gap: '10px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', marginBottom: '30px', flexWrap: 'wrap', alignItems: 'flex-end', border: '1px solid #eee' }}>
        
        {/* SEARCH FIELD */}
        <div style={{ position: 'relative', width: '120px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>{t.searchLabel}</label>
          <input 
            type="text" 
            placeholder={t.searchPlaceholder} 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '0.9rem' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>{t.building}</label>
          <div style={{ display: 'flex', gap: '5px' }}>
            {['WE2', 'WE3'].map(b => (
              <button key={b} onClick={() => toggleBuilding(b)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', background: selectedBuildings.includes(b) ? '#3498db' : '#fff', color: selectedBuildings.includes(b) ? '#fff' : '#555', fontWeight: 'bold', cursor: 'pointer' }}>{b}</button>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', width: '110px' }} ref={bedRef}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>{t.bedrooms}</label>
          <div onClick={() => setIsBedOpen(!isBedOpen)} style={{ padding: '8px', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>🛏️ {bedFilters.length > 0 ? `${bedFilters.length}` : t.any}</div>
          {isBedOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {['1', '1.1', '2', '2.1', '3', '0'].map(val => (
                <div key={val} onClick={(e) => toggleBed(e, val)} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', gap: '10px', background: bedFilters.includes(val) ? '#f0f7ff' : '#fff' }}>
                  <input type="checkbox" checked={bedFilters.includes(val)} readOnly />
                  <span style={{ fontSize: '0.85rem' }}>{val === '0' ? t.studio : val.replace('.1', ' + den')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* UPDATED BATHROOMS FILTER */}
        <div style={{ position: 'relative', width: '110px' }} ref={bathRef}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>{t.bathroomsFilter}</label>
          <div onClick={() => setIsBathOpen(!isBathOpen)} style={{ padding: '8px', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>🛁 {bathFilters.length > 0 ? `${bathFilters.length}` : t.any}</div>
          {isBathOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {['1', '2'].map(val => (
                <div key={val} onClick={(e) => toggleBath(e, val)} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', gap: '10px', background: bathFilters.includes(val) ? '#f0f7ff' : '#fff' }}>
                  <input type="checkbox" checked={bathFilters.includes(val)} readOnly />
                  <span style={{ fontSize: '0.85rem' }}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ position: 'relative', width: '130px' }} ref={monthRef}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>{t.moveIn}</label>
          <div onClick={() => setIsMonthOpen(!isMonthOpen)} style={{ padding: '8px', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>📅 {monthFilters.length > 0 ? `${monthFilters.length}` : t.any}</div>
          {isMonthOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px', zIndex: 100, maxHeight: '250px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {[{v:'ASAP',l:t.asap},{v:'NEXT',l:t.nextMonth},{v:'RANGE',l:t.range23}, ...dynamicMonths.map(m=>({v:m.value,l:m.label}))].map(opt => (
                <div key={opt.v} onClick={(e) => toggleMonth(e, opt.v)} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', gap: '10px', background: monthFilters.includes(opt.v) ? '#f0f7ff' : '#fff' }}>
                  <input type="checkbox" checked={monthFilters.includes(opt.v)} readOnly />
                  <span style={{ fontSize: '0.85rem' }}>{opt.l}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>{t.sortBy}</label>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ padding: '8px', borderRadius: '6px', width: '110px', border: '1px solid #ddd', cursor: 'pointer' }}>
            <option value="none">{t.default}</option>
            <option value="price-asc">{t.priceLow}</option>
            <option value="price-desc">{t.priceHigh}</option>
            <option value="date-asc">{t.dateSoon}</option>
            <option value="date-desc">{t.dateLate}</option>
          </select>
        </div>

        <button onClick={() => { setSearchQuery(''); setSelectedBuildings(['WE2', 'WE3']); setBedFilters([]); setBathFilters([]); setMonthFilters([]); setSortOrder('date-asc'); }} style={{ padding: '8px 15px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 'bold', color: '#666' }}>{t.reset}</button>
      </div>

      {/* FAVORITES BAR */}
      {favorites.length > 0 && (
        <div style={{ 
          position: 'fixed', 
          bottom: '30px', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          zIndex: 2000, 
          background: '#2c3e50', 
          color: '#fff', 
          padding: '15px 25px', 
          borderRadius: '50px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
          width: 'max-content',
          minWidth: '320px',
          gap: '20px'
        }}>
          <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            ❤️ {favorites.length} {favorites.length === 1 ? t.unitLabel : (lang === 'fr' ? 'unités' : 'units')}
          </span>
          <button onClick={handleEmailFavorites} style={{ background: '#27ae60', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '25px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', boxShadow: '0 4px 10px rgba(39, 174, 96, 0.4)' }}>
            {t.emailList}
          </button>
        </div>
      )}

      {/* GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '25px' }}>
        {displayedUnits.length === 0 ? (
           <div style={{ gridColumn: '1 / -1', padding: '2rem', textAlign: 'center', color: '#666', fontSize: '1.2rem' }}>{t.none}</div>
        ) : (
          displayedUnits.map((unit, index) => {
            const isFav = favorites.includes(unit.UnitNumber);
            return (
              <div key={index} style={{ border: '1px solid #eee', borderRadius: '15px', padding: '20px', position: 'relative', background: '#fff', transition: 'transform 0.2s' }}>
                <button onClick={() => toggleFavorite(unit.UnitNumber)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', zIndex: 10 }}>{isFav ? '❤️' : '🤍'}</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{t.unitLabel} {unit.UnitNumber}</h2>
                  <img src={unit.Building.includes('3') ? logoWe3 : logoWe2} alt="Logo" style={{ height: '30px' }} />
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '15px', color: '#2c3e50' }}>${unit.Rent} <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'normal' }}>{t.perMonth}</span></div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px', fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                  <div>🛏️ {formatBedrooms(unit.Bedrooms, lang)}</div>
                  <div>🛁 {unit.Bathrooms} {unit.Bathrooms.toString() === '1' ? t.bathLabel : t.bathsLabel}</div>
                  <div>📏 {unit.SquareFootage || "N/A"} {lang === 'en' ? 'sqft' : 'pi²'}</div>
                </div>
                
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '20px', borderTop: '1px solid #f5f5f5', paddingTop: '10px' }}>
                  📅 <strong>{t.available}:</strong> {formatFriendlyDate(unit.AvailabilityDate, lang)}
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a href={unit.UnitURL} target="_blank" rel="noopener noreferrer" style={{ flex: 1, textAlign: 'center', background: '#f8f9fa', color: '#333', padding: '10px 0', textDecoration: 'none', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.85rem', fontWeight: 'bold' }}>{t.viewPlan}</a>
                  <button onClick={() => { setTargetUnit(unit); setShowModal(true); }} style={{ flex: 1, background: '#3498db', color: 'white', border: 'none', padding: '10px 0', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}>{t.visitUnit}</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* POP-UP MODAL */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000, padding: '20px' }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '15px', width: '100%', maxWidth: '400px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginTop: 0 }}>{t.visitUnit} - {targetUnit?.UnitNumber}</h2>
            <form onSubmit={handleVisitRequest}>
              <input type="text" placeholder={t.namePlace} required style={{ width: '100%', padding: '12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid #ddd', boxSizing: 'border-box' }} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              <input type="tel" placeholder={t.phonePlace} style={{ width: '100%', padding: '12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid #ddd', boxSizing: 'border-box' }} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
              <input type="email" placeholder={t.emailPlace} style={{ width: '100%', padding: '12px', marginBottom: '20px', borderRadius: '8px', border: '1px solid #ddd', boxSizing: 'border-box' }} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', background: '#eee', cursor: 'pointer' }}>{t.cancel}</button>
                <button type="submit" disabled={isSubmitting} style={{ flex: 2, padding: '12px', borderRadius: '8px', border: 'none', background: '#27ae60', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{isSubmitting ? "..." : t.submit}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}